import { BinaryReader, fromBase64String } from "./binaryReader.ts";
import type { DataSource } from "../types/grid.ts";
import { reportIncompleteGrid } from "./incompleteData.ts";

export enum DataType {
  Nothing = 0,
  Boolean = 1,
  Byte = 8,
  Short = 16,
  Integer = 32,
  Long = 64,
  Single = 204,
  Double = 208,
  Decimal = 216,
  String = 100,
  DateTime_Binary = 101,
  DateTime_Ticks = 201,
  DateTime_UtcTicks = 202,
  Guid = 102,
  ByteArr = 103,
  Unused = 254,
  Unknown = 255,
}

/** The grid wire format this client asks for. 2 is plain JSON rows; the server answers 1 (the
 * binary binMatrix) to anyone who does not say, and this parser reads both. */
export const GRID_DATA_VERSION = 2;

function containsHeader(headers: string[], name: string): boolean {
  const upper = name.toUpperCase().replace(/^\[|\]$/g, "");
  return headers.some(
    (h) => h.toUpperCase().replace(/^\[|\]$/g, "") === upper
  );
}

// ── version 1: the binary binMatrix ─────────────────────────────────────────

function readItem(reader: BinaryReader): unknown {
  const datatype: DataType = reader.readByte();
  switch (datatype) {
    case DataType.Nothing:
      return null;
    case DataType.String:
      return reader.readString();
    case DataType.Boolean:
      return reader.readBoolean();
    case DataType.Byte:
      return reader.readByte();
    case DataType.Short:
      return reader.readInt16();
    case DataType.Integer:
      return reader.readInt32();
    case DataType.Long:
      return reader.readInt64();
    case DataType.Decimal:
      return reader.readDecimal();
    case DataType.Double:
      return reader.readDouble();
    case DataType.Single:
      return reader.readSingle();
    case DataType.DateTime_Binary:
      reader.skip(16);
      return null;
    // Exact milliseconds; the double arithmetic this used to do put some times 1 ms early.
    case DataType.DateTime_Ticks:
    case DataType.DateTime_UtcTicks:
      return new Date(reader.readTicksAsUnixMs());
    case DataType.Guid:
      return reader.readGuid();
    case DataType.ByteArr:
      return reader.readBytes(reader.readInt32());
    case DataType.Unknown: {
      console.warn("Unknown type:", reader.readString());
      return reader.readString();
    }
    default:
      throw new Error("UnknownTypeException " + datatype);
  }
}

function fixSummary(data: DataSource): void {
  if (!data.footerMatrix || data.footerMatrix.length === 0) return;
  const brow = data.footerMatrix[0];
  if (!brow) return;
  const reader = new BinaryReader(brow);
  const drow: unknown[] = [];
  while (reader.hasData()) {
    try {
      drow.push(readItem(reader));
    } catch (e) {
      console.error("Error reading footer row:", e);
      break;
    }
  }
  data.summary = {
    sum: drow.slice(0, drow.length / 2),
    avg: drow.slice(drow.length / 2, drow.length),
  };
  data.footerMatrix = null;
}

function parseV1(data: DataSource, count: number): void {
  for (let r = 0; r < (data.binMatrix?.length ?? 0); r++) {
    const brow = data.binMatrix![r];
    if (!brow) continue;
    const reader = new BinaryReader(brow);
    const drow: unknown[] = [];
    for (let i = 0; i < count; i++) {
      try {
        drow.push(readItem(reader));
      } catch (e) {
        console.error("Error reading data row:", e);
        break;
      }
    }
    data.matrix.push(drow);
  }
  data.binMatrix = null;

  if (data.footerMatrix) {
    fixSummary(data);
  }
}

// ── version 2: JSON rows, typed per column ──────────────────────────────────

/**
 * One cell, as version 1 would have produced it — so everything downstream of the parser sees the
 * same values whichever format arrived. `decimal` is a number here; its exact text is kept in
 * `decimalText` by the caller.
 */
function decodeV2(type: string, value: unknown): unknown {
  if (value === null || value === undefined) return null;
  switch (type) {
    case "decimal":
      return Number(value as string);
    case "datetime":
      return new Date(value as number);
    case "bytes":
      return fromBase64String(value as string);
    case "variant": {
      const [actual, inner] = value as [string, unknown];
      return decodeV2(actual, inner);
    }
    default:
      return value; // int, long, double, string, bool, guid
  }
}

/** The exact text of a decimal cell, variant cells included; null for anything else. */
function decimalTextOf(type: string, value: unknown): string | null {
  if (value === null || value === undefined) return null;
  if (type === "decimal") return value as string;
  if (type === "variant") {
    const [actual, inner] = value as [string, unknown];
    return actual === "decimal" && inner != null ? (inner as string) : null;
  }
  return null;
}

function parseV2(data: DataSource, count: number): void {
  const types = data.types ?? [];
  const rows = data.rows ?? [];
  const width = Math.min(count, types.length);

  // Exact decimal text, by column, for the columns that can hold one. A JavaScript number is a
  // double; this is the value the database actually stored, for code that needs Big rather than
  // a rounded neighbour of it.
  const decimalText: Record<number, (string | null)[]> = {};
  for (let c = 0; c < width; c++) {
    if (types[c] === "decimal" || types[c] === "variant") decimalText[c] = new Array(rows.length).fill(null);
  }

  for (let r = 0; r < rows.length; r++) {
    const row = rows[r]!;
    const drow = new Array<unknown>(width);
    for (let c = 0; c < width; c++) {
      const value = row[c];
      drow[c] = decodeV2(types[c]!, value);
      const exact = decimalText[c];
      if (exact) exact[r] = decimalTextOf(types[c]!, value);
    }
    data.matrix.push(drow);
  }
  data.decimalText = decimalText;
  data.rows = null;

  const footer = data.footerRows?.[0];
  if (footer) {
    const ftypes = data.footerTypes ?? [];
    const drow = footer.map((v, i) => decodeV2(ftypes[i] ?? "string", v));
    data.summary = {
      sum: drow.slice(0, drow.length / 2),
      avg: drow.slice(drow.length / 2, drow.length),
    };
  }
  data.footerRows = null;
}

/**
 * Turns a grid response into `data.matrix` — one array of values per row — whichever wire format
 * it arrived in. Version 1 (`binMatrix`) and version 2 (`rows`) produce the same matrix and the
 * same footer summary; `gridDataParser.test.ts` holds them to it against fixtures the server
 * generates from one result set.
 */
export function parseGridData(data: DataSource): void {
  if (!data || data.matrix) return;
  data.matrix = [];
  const headers = data.attributes.header;
  const count = headers.length - (containsHeader(headers, "iRowNumber") ? 1 : 0);

  if (data.dataVersion === 2) parseV2(data, count);
  else parseV1(data, count);

  // A streamed response that failed part-way. The rows are kept — they are correct — and the user
  // is told, whoever asked for them. Once per response: a parsed response returns early above.
  if (data.incomplete) reportIncompleteGrid(data.incomplete);
}

/**
 * The exact stored value of a decimal cell, as text ("12.50"), when the response carried it —
 * version 2 does, version 1 cannot. Null for a null cell, a non-decimal column, or a version 1
 * response. Use it where a double is not good enough, e.g. `new Big(getExactDecimal(...))`.
 */
export function getExactDecimal(data: DataSource, row: number, column: number): string | null {
  return data.decimalText?.[column]?.[row] ?? null;
}
